import React from "react";
import { ClipboardIcon, Download, Edit, Share, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { Item } from "@/types/fse";
import { RenameModal } from "../modals/renameModal";
import { ShareModal } from "../modals/shareModal";
import { DeleteModal } from "@/views/drive/modals/deleteModal";
import { SHAREPOINT_FOLDER_NAME } from "@/views/drive/consts";
import * as analytics from "@/services/analytics";
import { FastApiErrorResponse } from "@/api/base";
import { toast } from "react-toastify";
import { useCreateChronologiesMutation } from "@/api/chronology";
import TopBarProgress from "react-topbar-progress-indicator";

interface Props {
  item: Item;
  variant: "empty" | "ghost";
  children: React.ReactNode;
  onRemove?: () => void;
  isFolder?: boolean;
  isRootFolder?: boolean;
}

export const DriveRowMenu: React.FC<Props> = ({ isRootFolder, variant, item, children, onRemove, isFolder }) => {
  const [isRenameOpen, setIsRenameOpen] = React.useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);
  const [isShareOpen, setIsShareOpen] = React.useState(false);
  const user = useAuthenticatedUser();

  const isReadOnly = !item.permissions.some(
    permission => permission.role === "owner" && permission.user_id === user.id,
  );
  const [handleCreateChronologies, { isLoading }] = useCreateChronologiesMutation();

  const hasChronology = (item.transactions?.chronology?.progress || 0) >= 1;

  const handleGenerate = async () => {
    try {
      await handleCreateChronologies(item.id).unwrap();
      analytics.chronologyCreate(item.id);
      toast.success("Regenerate chronology scheduled");
    } catch (error) {
      if ((error as FastApiErrorResponse).data.err_type === "NoDocumentsToProcessError") {
        toast.error("Can't create chronology for this folder, folder is empty");
      } else {
        toast.error("Error while creating chronology");
      }
    }
  };
  const handleExport = () => {
    analytics.folderExport(item.id);

    document.location.href = `/pf-document/export/${item.id}`;
  };

  return (
    <>
      <RenameModal setIsOpen={setIsRenameOpen} isOpen={isRenameOpen} item={item} />
      <DeleteModal setIsOpen={setIsRemoveOpen} isOpen={isRemoveOpen} item={item} onRemove={onRemove} />
      {isShareOpen && <ShareModal setIsOpen={setIsShareOpen} isOpen={isShareOpen} item={item} />}
      {isLoading && <TopBarProgress />}

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} className="h-8 w-8 p-0">
            {children}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isRootFolder && (
            <>
              <DropdownMenuItem disabled={isReadOnly} onSelect={() => setIsRemoveOpen(true)} className="cursor-pointer">
                <Trash2 className="mr-2 h-4 w-4" />
                Remove
              </DropdownMenuItem>
              <DropdownMenuItem disabled={isReadOnly} onSelect={() => setIsRenameOpen(true)} className="cursor-pointer">
                <Edit className="mr-2 h-4 w-4" />
                Rename
              </DropdownMenuItem>
              {hasChronology && (
                <DropdownMenuItem onSelect={handleGenerate} className="cursor-pointer">
                  <ClipboardIcon className="mr-2 h-4 w-4" />
                  Regen Chronology
                </DropdownMenuItem>
              )}
              {item.name !== SHAREPOINT_FOLDER_NAME && (
                <DropdownMenuItem
                  disabled={isReadOnly}
                  onSelect={() => setIsShareOpen(true)}
                  className="cursor-pointer"
                >
                  <Share className="mr-2 h-4 w-4" />
                  Share
                </DropdownMenuItem>
              )}
            </>
          )}
          {isFolder && (
            <DropdownMenuItem className="cursor-pointer" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
